import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../redux/TranslationHelper';
import { 
  selectUser,
  selectCurrentLanguage,
  selectIsUserLoggedIn
} from '../../redux/WithcookieSlice';
import { 
  isExternalSkin, 
  createExternalSkinWrapper 
} from './ComponentSkinLoader';
import classNames from 'classnames';

/**
 * Hook to prepare skin props from component data
 * @param {import('../../types/component-skin').ComponentData} componentData 
 * @param {import('../../types/component-skin').ComponentRenderMode} mode 
 * @param {Record<string, any>} actions 
 * @param {Record<string, any>} options 
 * @param {Object} editorProps 
 * @param {Record<string, any>} logicData 
 * @returns {import('../../types/component-skin').ComponentSkinProps}
 */
function useComponentSkinProps(componentData, mode, actions, options, editorProps = {}, logicData = {}, otherLogicData = {}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Select app data
  const user = useSelector(selectUser);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const withcookieData = useSelector(state => state.withcookie?.withcookieData || {});
  const company = withcookieData?.skin?.company || null;
  const theme = withcookieData?.skin?.theme?.colorset || {};
  
  // Currency formatter
  const formatCurrency = (amount, currency = 'KRW') => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Date formatter
  const formatDate = (date, format = 'YYYY-MM-DD') => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes);
  };
  
  // Asset URL helper
  const getAssetUrl = (path) => {
    if (path.startsWith('http')) return path;
    return process.env.PUBLIC_URL + path;
  };
  
  // Class name helper (like clsx)
  const cx = (...classes) => {
    return classNames(...classes);
  };
  
  const skinProps = {
    data: {
      ...(componentData.data || componentData),  // 기존 componentData
      ...logicData,  // useLogic에서 반환된 data 객체
      ...otherLogicData,  // useLogic에서 반환된 기타 데이터
      // DOM 속성들 추가 (External Skin에서 사용하기 위해)
      id: componentData?.id,
      className: componentData?.componentProps?.className || componentData?.className || '',
      style: componentData?.style || {},
      // componentProps를 명시적으로 전달
      componentProps: componentData?.componentProps || {},
      viewMode: componentData?.viewMode || 'pc',
      // External Skin 호환성을 위한 props 객체
      props: {
        id: componentData?.id,
        className: componentData?.componentProps?.className || componentData?.className || '',
        style: componentData?.style || {}
      }
    },
    actions: actions || {},
    options: options || {},
    mode,
    utils: {
      t,
      navigate,
      formatCurrency,
      formatDate,
      getAssetUrl,
      cx
    },
    app: {
      user,
      company,
      currentLanguage,
      isUserLoggedIn,
      theme
    }
  };
  

  // 디버깅: MainBanner의 경우 skinProps.data 확인
  if (componentData && componentData.type === 'MAIN_BANNER') {
    // 디버그 로그 추가

    // banners가 배열이 아닌 경우 빈 배열로 설정
    if (!Array.isArray(skinProps.data.banners)) {
      console.warn('[useComponentSkinProps] MainBanner banners is not an array, setting to empty array');
      skinProps.data.banners = [];
    }
  }

  // 디버깅: TrendingItems의 경우 skinProps.data 확인
  if (componentData && componentData.type === 'TRENDING_ITEMS') {
    // 디버그 로그 추가

    // trendingItems가 배열이 아닌 경우 빈 배열로 설정
    if (!Array.isArray(skinProps.data.trendingItems)) {
      console.warn('[useComponentSkinProps] TrendingItems trendingItems is not an array, setting to empty array');
      skinProps.data.trendingItems = [];
    }
  }
  
  // Add editor props only in editor mode
  if (mode === 'editor' && editorProps) {
    skinProps.editor = editorProps;
  }
  
  return skinProps;
}

/**
 * Component Skin Wrapper
 * Renders a component using either internal or external skin
 * 
 * @param {Object} props
 * @param {import('../../types/component-skin').SkinnableComponent} props.component - Component definition
 * @param {import('../../types/component-skin').ComponentData} props.componentData - Component instance data  
 * @param {string} [props.skinId] - Selected skin ID (defaults to component.defaultSkin)
 * @param {import('../../types/component-skin').ComponentRenderMode} [props.mode='preview'] - Render mode
 * @param {Object} [props.editorProps] - Props for editor mode
 */
const ComponentSkinWrapper = memo(function ComponentSkinWrapper({ 
  component, 
  componentData, 
  skinId,
  mode = 'preview',
  editorProps
}) {
  // Get component logic/actions if available - must be called before any early returns
  let logicResult = {};
  try {
    if (component && component.useLogic && typeof component.useLogic === 'function') {
      logicResult = component.useLogic(componentData, mode) || {};
    }
  } catch (error) {
    console.error('[ComponentSkinWrapper] Error in useLogic:', error);
  }
  
  const { actions = {}, data: logicData = {}, ...otherLogicData } = logicResult;
  
  // Merge options with component defaults
  let mergedOptions = {};
  try {
    const defaults = {};
    
    // Process component options
    if (component && component.options && Array.isArray(component.options)) {
      component.options.forEach(opt => {
        if (opt && typeof opt === 'object') {
          // key 또는 name 속성을 사용
          const key = opt.key || opt.name;
          const defaultValue = opt.default !== undefined ? opt.default : opt.defaultValue;
          if (key && defaultValue !== undefined) {
            defaults[key] = defaultValue;
          }
        }
      });
    }
    
    mergedOptions = {
      ...defaults,
      ...(component?.defaultProps?.componentProps || {}),  // defaultProps의 componentProps를 평탄화
      ...(componentData?.componentProps || {})  // componentData의 componentProps를 평탄화
    };
  } catch (error) {
    console.error('[ComponentSkinWrapper] Error processing options:', error);
    mergedOptions = componentData?.props || {};
  }
  
  // Prepare skin props - must be called before any early returns
  const skinProps = useComponentSkinProps(
    componentData,
    mode,
    actions,
    mergedOptions,
    editorProps,
    logicData,
    otherLogicData
  );
  
  // Early validation checks after hooks
  if (!component) {
    console.error('[ComponentSkinWrapper] component prop is required');
    return <div>Component not provided</div>;
  }
  
  if (!componentData) {
    console.error('[ComponentSkinWrapper] componentData prop is required');
    return <div>Component data not provided</div>;
  }
  
  // Use selected skin or default
  const selectedSkinId = skinId || componentData?.componentProps?.skin || componentData?.props?.skin || component.defaultSkin;
  

  // Check if using legacy render method
  if (!selectedSkinId && !component.internalSkins) {
    // Fallback to legacy EditorView/PreviewView
    if (mode === 'editor' && component.EditorView) {
      const EditorView = component.EditorView;
      return <EditorView {...componentData} {...editorProps} />;
    } else if (mode === 'preview' && component.PreviewView) {
      const PreviewView = component.PreviewView;
      return <PreviewView {...componentData} />;
    }
    return <div>No skin available for component: {component.type}</div>;
  }
  
  // Determine if skin is external
  if (isExternalSkin(selectedSkinId)) {

    try {
      // Use external skin
      const ExternalSkin = createExternalSkinWrapper(selectedSkinId);
      return <ExternalSkin {...skinProps} />;
    } catch (error) {
      console.error(`[ComponentSkinWrapper] External skin error for ID ${selectedSkinId}:`, error);

      // Fallback to basic internal skin
      const BasicSkin = component.internalSkins?.['basic'];
      if (BasicSkin) {
        return <BasicSkin {...skinProps} />;
      }
      
      // Last resort: show error message
      return (
        <div style={{ 
          padding: '20px', 
          border: '2px solid #f56565', 
          borderRadius: '8px', 
          backgroundColor: '#fed7d7',
          color: '#9b2c2c'
        }}>
          <h3>외부 스킨 로딩 실패</h3>
          <p>스킨 ID: {selectedSkinId}</p>
          <p>기본 스킨으로 전환하거나 관리자에게 문의하세요.</p>
        </div>
      );
    }
  }
  
  // Use internal skin
  const InternalSkin = component.internalSkins?.[selectedSkinId];
  
  if (!InternalSkin) {
    console.error(`Skin "${selectedSkinId}" not found for component "${component.type}"`, {
      selectedSkinId,
      availableSkins: Object.keys(component.internalSkins || {}),
      defaultSkin: component.defaultSkin
    });
    // Try fallback to default skin
    const DefaultSkin = component.internalSkins?.[component.defaultSkin];
    if (DefaultSkin) {
      return <DefaultSkin {...skinProps} />;
    }
    return (
      <div style={{ 
        padding: '20px', 
        background: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        color: '#c00'
      }}>
        <strong>스킨을 찾을 수 없습니다</strong>
        <div style={{ marginTop: '10px', fontSize: '14px' }}>
          컴포넌트: {component.type}<br />
          요청된 스킨: {selectedSkinId}
        </div>
      </div>
    );
  }
  
  return <InternalSkin {...skinProps} />;
});

export default ComponentSkinWrapper;